import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, ArrowLeft, Shield, DollarSign, Zap, CheckCircle, Users,
  Briefcase, FileText, CreditCard, TrendingUp, Building2, ClipboardCheck,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-80px' } };

// ── Annotation Callout (light theme variant) ──
function Callout({ x, y, label, tone = 'purple', arrow = 'left' }) {
  const tones = {
    purple: 'bg-[#6C3AED] text-white shadow-[0_10px_40px_-8px_rgba(108,58,237,0.5)]',
    emerald: 'bg-emerald-600 text-white shadow-[0_10px_40px_-8px_rgba(16,185,129,0.5)]',
    amber: 'bg-amber-500 text-white shadow-[0_10px_40px_-8px_rgba(245,158,11,0.5)]',
  };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="absolute z-20 flex items-center gap-2"
      style={{ left: `${x}%`, top: `${y}%` }}>
      {arrow === 'left' && <div className="w-8 sm:w-12 h-[2px]" style={{ background: tone === 'purple' ? '#6C3AED' : tone === 'emerald' ? '#059669' : '#f59e0b' }} />}
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${tones[tone]}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
        {label}
      </span>
      {arrow === 'right' && <div className="w-8 sm:w-12 h-[2px]" style={{ background: tone === 'purple' ? '#6C3AED' : tone === 'emerald' ? '#059669' : '#f59e0b' }} />}
    </motion.div>
  );
}

function FeatureRow({ eyebrow, title, body, image, callouts = [], reverse = false, bullets = [] }) {
  return (
    <motion.div {...fadeUp} transition={{ duration: 0.6 }}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[&>div:first-child]:order-2' : ''}`}>
      <div className="lg:col-span-5 space-y-5">
        <p className="text-[#6C3AED] text-xs font-bold tracking-[0.2em] uppercase">{eyebrow}</p>
        <h3 className="text-3xl sm:text-4xl font-bold text-[#0B0F19] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
          {title}
        </h3>
        <p className="text-slate-500 text-base leading-relaxed">{body}</p>
        {bullets.length > 0 && (
          <ul className="space-y-3 pt-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#6C3AED]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-[#6C3AED]" />
                </div>
                <span className="text-slate-600 text-sm">{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="lg:col-span-7 relative">
        <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.25)] bg-white">
          <img src={image} alt="" loading="lazy" className="w-full h-auto block" />
          {callouts.map((c, i) => <Callout key={i} {...c} />)}
        </div>
        <div className="absolute -inset-4 bg-[#6C3AED]/5 blur-3xl -z-10 rounded-3xl" />
      </div>
    </motion.div>
  );
}

function Step({ n, icon: Icon, title, body }) {
  return (
    <div className="relative">
      <div className="w-12 h-12 rounded-xl bg-[#6C3AED]/10 border border-[#6C3AED]/20 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#6C3AED]" />
      </div>
      <p className="text-[#6C3AED] text-[10px] font-bold tracking-[0.2em] uppercase mb-1.5">Step {n}</p>
      <h4 className="text-[#0B0F19] text-base font-semibold mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h4>
      <p className="text-slate-500 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

export default function AgenciesLanding() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#0B0F19]" data-testid="agencies-landing-page">
      {/* HEADER — light variant */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-[#6C3AED] flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="text-[#0B0F19] font-bold text-lg tracking-tight">MyLikeness</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Button onClick={() => navigate(user.role === 'creator' ? '/creator' : user.role === 'admin' ? '/admin' : '/agency')}
                className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white text-sm font-semibold h-10 px-5 rounded-full">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-[#0B0F19] transition-colors hidden sm:block">Log in</Link>
                <Button onClick={() => navigate('/signup')} className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white text-sm font-semibold h-10 px-6 rounded-full" data-testid="nav-get-started-btn">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-70 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#6C3AED]/8 blur-[120px] rounded-full" />
          <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-[#A78BFA]/8 blur-[100px] rounded-full" />
        </div>
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-[#0B0F19] transition-colors mb-6" data-testid="back-to-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl">
            <p className="text-[#6C3AED] text-xs font-bold tracking-[0.25em] uppercase mb-6">For Agencies</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              The right likeness.<br />
              For the <span className="italic text-[#6C3AED]">right result.</span>
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed max-w-xl mb-10">
              Book verified creator likenesses, lock in rights up-front, and ship campaigns in days — not weeks. Every deal is contract-backed, escrow-secured, and fully auditable.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Button onClick={() => navigate('/signup')} data-testid="hero-cta-signup"
                className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white h-12 px-7 rounded-full text-sm font-bold shadow-lg shadow-[#6C3AED]/25">
                Start your agency workspace <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button onClick={() => navigate('/creators')} className="text-slate-500 hover:text-[#6C3AED] text-sm font-semibold inline-flex items-center gap-1.5 transition-colors">
                Are you a creator? <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS — 4 steps */}
      <section className="py-16 border-t border-slate-200 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="mb-14 max-w-2xl">
            <p className="text-[#6C3AED] text-xs font-bold tracking-[0.2em] uppercase mb-4">How It Works</p>
            <h2 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              From brief to license, <span className="italic text-[#6C3AED]">shipped.</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <Step n="1" icon={Briefcase} title="Create a campaign" body="Set the brief, channels, territory and dates. Duplicate previous campaigns to spin up new work in seconds." />
            <Step n="2" icon={Users} title="Find & invite creators" body="Connect via code, invite off-platform talent by email, or browse verified creators already on the platform." />
            <Step n="3" icon={FileText} title="Send crystal-clear proposals" body="Rights, exclusivity, deliverables and payment terms — all captured in one signed agreement." />
            <Step n="4" icon={CreditCard} title="Fund. Deliver. License." body="Pay in-app with a saved card, upload content for creator approval, and receive a verifiable license certificate." />
          </div>
        </div>
      </section>

      {/* FEATURE DEEP DIVES */}
      <section className="py-24 border-t border-slate-200">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 space-y-32">
          <FeatureRow
            eyebrow="Central Command"
            title="One dashboard. Every campaign. Zero spreadsheets."
            body="Every campaign, proposal, licence and dollar in one view. Live counters, action-required prompts, licence expiry countdowns, spend charts, and top-creator leaderboards — no more chasing status across five tools."
            image="/marketing/agency-dashboard.jpeg"
            bullets={[
              '5 headline metrics at a glance — active campaigns, connected creators, pending proposals, active licences, monthly spend',
              'Action-required panel highlights the exact 3 things that need your attention',
              'Licence expiry countdowns so no active usage right ever lapses',
            ]}
            callouts={[
              { x: 40, y: 12, label: 'Live counters', tone: 'purple' },
              { x: 50, y: 27, label: 'Active campaigns', tone: 'emerald' },
              { x: 20, y: 78, label: 'Spend at a glance', tone: 'amber' },
            ]}
          />

          <FeatureRow
            eyebrow="Campaign Management"
            title="Filter, duplicate, ship."
            body="Filter by status, sort by newest, search by name. Every campaign shows deal counts, funding progress, and campaign type. Duplicate a winning campaign to launch the next in seconds."
            image="/marketing/agency-campaigns.jpeg"
            reverse
            bullets={[
              'Status filters: Active, Draft, Pending, Content Review, Archived',
              'Duplicate campaign — instant re-run of what works',
              'Value + funding progress visible on every row',
            ]}
            callouts={[
              { x: 45, y: 10, label: 'Filter by state', tone: 'purple' },
              { x: 30, y: 30, label: 'Creator licensing progress', tone: 'emerald' },
            ]}
          />

          <FeatureRow
            eyebrow="Creator Discovery"
            title="Verified talent, on tap."
            body="Browse creators who have connected to your agency, invite new ones by email or unique code, and see status at a glance — active, pending, engagements, licenses, awaiting approval."
            image="/marketing/agency-creators.jpeg"
            bullets={[
              'Invite by email — creators receive a branded onboarding email',
              'Verified badge on every ID-checked creator',
              'Live status: connected, pending, active engagements, licenses',
            ]}
            callouts={[
              { x: 62, y: 5, label: 'Invite off-platform', tone: 'purple' },
              { x: 22, y: 46, label: 'Verified creators', tone: 'emerald' },
            ]}
          />

          <FeatureRow
            eyebrow="Finance & Reporting"
            title="Every dollar, every deal, traced."
            body="See total funded, funds held in escrow, payouts released, and pending funding — all in one view. Break down spend by campaign, by creator, or by time period. Nothing walks out the door without a trail."
            image="/marketing/agency-finance.jpeg"
            reverse
            bullets={[
              'Escrow-secured payments — funds held until creator approves',
              'Recent transaction feed with status per line',
              'Top campaigns and top creators by spend',
            ]}
            callouts={[
              { x: 40, y: 12, label: 'Held safely in escrow', tone: 'purple' },
              { x: 10, y: 62, label: 'Full audit trail', tone: 'emerald' },
            ]}
          />

          <FeatureRow
            eyebrow="Team & Workspace"
            title="Roles, permissions, and payment guardrails."
            body="Invite team members with granular roles — read-only, legal, payments admin, campaign manager. Team mode enables per-role notification routing, payment/billing gates, and a per-user audit trail. Solo mode strips it all back."
            image="/marketing/agency-settings.jpeg"
            bullets={[
              'Solo or Team mode — switch at any time',
              'Multi-role permissions with role-union access',
              'Per-user audit trail, notification routing, payment controls',
            ]}
            callouts={[
              { x: 45, y: 15, label: 'Team mode active', tone: 'emerald' },
              { x: 32, y: 45, label: 'Granular roles', tone: 'purple' },
            ]}
          />
        </div>
      </section>

      {/* CLIENT-CONFIDENCE BAR */}
      <section className="py-16 border-t border-slate-200 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-10">
            <p className="text-[#6C3AED] text-xs font-bold tracking-[0.2em] uppercase mb-3">Confidence built in</p>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Everything your legal team wants to hear.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Shield, title: 'Signed PDF agreements', body: 'Every deal produces a versioned agreement PDF — auditable, downloadable, verifiable.' },
              { icon: DollarSign, title: 'Escrow-first funding', body: 'Funds are secured before creation begins and only released when the creator approves the work.' },
              { icon: CheckCircle, title: 'Rights, spelled out', body: 'Platforms, territory, exclusivity, duration, moral rights, and permitted use — all captured up front.' },
              { icon: TrendingUp, title: 'Financial reporting', body: 'Transactions, campaign spend, creator spend, and platform fees exportable in one place.' },
              { icon: Zap, title: 'In-app payments', body: 'Add a card once via Stripe. Fund deals, subscribe, and process refunds without leaving the platform.' },
              { icon: ClipboardCheck, title: 'License vault', body: 'Every active license kept in a searchable vault so your team can prove usage rights any time.' },
            ].map((f, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="p-5 rounded-2xl bg-white border border-slate-200 hover:border-[#6C3AED]/30 hover:shadow-lg transition-all">
                <div className="w-10 h-10 rounded-xl bg-[#6C3AED]/10 flex items-center justify-center mb-3">
                  <f.icon className="w-4.5 h-4.5 text-[#6C3AED]" />
                </div>
                <h4 className="text-[#0B0F19] font-semibold text-base mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>{f.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-20 border-t border-slate-200">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <div className="w-16 h-16 rounded-2xl bg-[#6C3AED]/10 flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-7 h-7 text-[#6C3AED]" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Start your <span className="italic text-[#6C3AED]">workspace.</span>
            </h2>
            <p className="text-slate-500 text-lg mb-8 max-w-xl mx-auto">
              Free to set up. Invite your team, run your first campaign, and pay only when you fund a deal.
            </p>
            <Button onClick={() => navigate('/signup')} data-testid="footer-cta-signup"
              className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white h-14 px-9 rounded-full text-base font-bold shadow-lg shadow-[#6C3AED]/25">
              Start your agency workspace <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-slate-200 bg-[#0B0F19]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">© {new Date().getFullYear()} MyLikeness · Licensed presence, verified proof</p>
          <div className="flex items-center gap-5 flex-wrap justify-center md:justify-end">
            <Link to="/" className="text-white/40 hover:text-white/80 text-xs transition-colors">Home</Link>
            <Link to="/creators" className="text-white/40 hover:text-white/80 text-xs transition-colors">For Creators</Link>
            <Link to="/terms" className="text-white/40 hover:text-white/80 text-xs transition-colors">Terms</Link>
            <Link to="/privacy" className="text-white/40 hover:text-white/80 text-xs transition-colors">Privacy</Link>
            <Link to="/acceptable-use" className="text-white/40 hover:text-white/80 text-xs transition-colors">Acceptable Use</Link>
            <Link to="/trust" className="text-white/40 hover:text-white/80 text-xs transition-colors">Trust &amp; Safety</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
